// Kanıt paneli satırı (EvidencePanel.tsx'ten ayrıldı — güvenlik/UX taraması 2026-10-08, 600 satır
// sınırı; davranış birebir): tür rozeti, dosya adı + konum, tarama rozeti, "kaynak dosya" rozeti,
// Aç (yalnız clean) + Bağı kaldır + saklama seçimi. `FormEvidenceBadge` mevcut dosya listesinde de
// kullanılır (TODO-EVIDENCE-01).

import { kpiEvidenceDownloadUrl, type KpiLinkedEvidence } from "../../api/kpi-evidence";
import { portalEvidenceDownloadUrl } from "../../api/portal-kpi";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import {
  canOpenEvidence,
  fileKindLabel,
  formatFileSize,
  formatHistoryDate,
  formatLocator,
  formLinkLabels,
  normalizeRetentionPolicy,
  relationLabel,
  retentionOptions,
  scanBadge,
} from "../../lib/kpi-evidence-ui";
import { inputStyle } from "./kpi-form-styles";

const C = KPI_EVIDENCE_COPY.panel;
export type PanelItem = KpiLinkedEvidence & { downloadable?: boolean };

/** TODO-EVIDENCE-01 — "form kanıtı · <soru kodu>" rozeti (mevcut dosya listesi + satır). */
export function FormEvidenceBadge({ label }: { label: string }): JSX.Element {
  return (
    <span
      data-testid="kpi-evidence-form-badge"
      style={{
        display: "inline-block",
        padding: "0 6px",
        borderRadius: 999,
        fontSize: "0.6rem",
        fontWeight: 700,
        marginLeft: 6,
        background: "color-mix(in srgb, var(--color-primary) 10%, white)",
        color: "var(--color-primary)",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

export function EvidenceRow(p: {
  item: PanelItem;
  readOnly: boolean;
  portal: boolean;
  onUnlink: () => void;
  onRetention: (policy: string) => void;
}): JSX.Element {
  const { item, readOnly, portal, onUnlink, onRetention } = p;
  const scan = scanBadge(item.scanStatus, item.purgedAt);
  const formBadges = portal ? [] : formLinkLabels(item.formLinks);
  const retention = normalizeRetentionPolicy(item.retentionPolicy);
  // C2/DR9 — portalda indirilemeyen dosya: "Kaynak: … (danışman dosyası)", Aç yok
  const consultantFile = portal && item.downloadable === false;
  const openable = canOpenEvidence(item.scanStatus, item.purgedAt) && !consultantFile;
  // güvenlik/UX taraması 2026-10-08 (S-L2): "Aç" adresi istemcide kurulur — sunucudan gelen
  // `downloadUrl` dizesi kullanılmaz (açık yönlendirme yüzeyi kapanır).
  const href = portal ? portalEvidenceDownloadUrl(item.id) : kpiEvidenceDownloadUrl(item);
  const kind = fileKindLabel(item.mimeType, item.fileName);
  const locator = formatLocator(item.locator);
  const blocked = scan.tone === "blocked";
  return (
    <li
      data-testid="kpi-evidence-row"
      className="flex gap-3 py-2"
      style={{ borderBottom: "1px solid var(--color-border)", alignItems: "flex-start" }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 44,
          height: 36,
          borderRadius: 8,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 700,
          background: blocked
            ? "color-mix(in srgb, var(--color-error) 10%, white)"
            : scan.tone === "pending"
              ? "var(--color-warning-bg)"
              : "color-mix(in srgb, var(--color-primary) 10%, white)",
          color: blocked
            ? "var(--color-error)"
            : scan.tone === "pending"
              ? "var(--color-warning-text)"
              : "var(--color-primary)",
        }}
      >
        {kind}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="text-sm"
          style={{
            fontWeight: 600,
            color: "var(--color-text)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: blocked ? "line-through" : undefined,
          }}
          title={item.fileName}
        >
          {consultantFile ? "Kaynak: " : ""}
          {item.fileName}
          {consultantFile ? (
            <span className="ml-1" style={{ fontWeight: 400, color: "var(--color-text-3)" }}>
              (danışman dosyası)
            </span>
          ) : null}
          {locator ? (
            <span className="ml-1" style={{ fontWeight: 400, color: "var(--color-text-2)" }}>
              · {locator}
            </span>
          ) : null}
        </div>
        <div className="text-xs" style={{ color: "var(--color-text-3)", lineHeight: 1.6 }}>
          {scan.tone !== "ok" ? (
            <span
              title={item.scanStatus === "error" ? KPI_EVIDENCE_COPY.scan.errorHint : undefined}
              style={{
                display: "inline-block",
                padding: "0 6px",
                borderRadius: 999,
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                marginRight: 6,
                background: blocked
                  ? "color-mix(in srgb, var(--color-error) 10%, white)"
                  : "var(--color-warning-bg)",
                color: blocked ? "var(--color-error)" : "var(--color-warning-text)",
                border: blocked ? "1px solid transparent" : "1px solid var(--color-warning-border)",
              }}
            >
              {scan.text}
            </span>
          ) : null}
          {formBadges.map((label) => (
            <FormEvidenceBadge key={label} label={label} />
          ))}
          {item.relationType === "import_source" ? (
            <span
              style={{
                display: "inline-block",
                padding: "0 6px",
                borderRadius: 999,
                fontSize: "0.6rem",
                fontWeight: 700,
                marginRight: 6,
                background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                color: "var(--color-primary)",
              }}
            >
              {relationLabel(item.relationType)}
            </span>
          ) : (
            <span>{relationLabel(item.relationType)} · </span>
          )}
          {formatFileSize(item.sizeBytes)} · {formatHistoryDate(item.linkedAt)}
        </div>
      </div>
      <div
        className="flex flex-col items-end text-xs"
        style={{ flexShrink: 0, gap: 2, lineHeight: 1.6 }}
      >
        {openable ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--color-primary)", fontWeight: 600 }}
          >
            {C.open}
          </a>
        ) : consultantFile ? null : (
          <span
            aria-disabled="true"
            title={C.openBlocked}
            style={{ color: "var(--color-text-3)", cursor: "not-allowed" }}
          >
            {C.open}
          </span>
        )}
        {!readOnly ? (
          <button
            type="button"
            onClick={onUnlink}
            style={{ color: "var(--color-text-2)", minHeight: 24, textAlign: "right" }}
          >
            {C.unlink}
          </button>
        ) : null}
        {!readOnly && !portal ? (
          <label
            className="flex items-center gap-1"
            style={{ color: "var(--color-text-3)", fontSize: "0.7rem" }}
          >
            {C.retentionLabel}
            <select
              data-testid="kpi-evidence-retention"
              aria-label={`${item.fileName} — ${C.retentionLabel}`}
              value={retention}
              onChange={(e) => onRetention(e.target.value)}
              style={{
                ...inputStyle,
                minHeight: 24,
                padding: "0 4px",
                fontSize: "0.7rem",
                width: "auto",
              }}
            >
              {retentionOptions(retention).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    </li>
  );
}
